/**
 * Resolves a canonical MongoDB `locations._id` from a list/itinerary slot for
 * use with the location-save APIs.
 *
 * Slots typically carry:
 *   - `locationData._id` / `locationData.location_id`  →  already a Mongo ObjectId
 *   - `placeId` / `place.google_place_id`              →  Google Place ID, needs resolving
 *
 * The helper tries the direct id path first (fast, no network). When only a
 * Google Place ID is available it calls `fetchPersistedLocationByGooglePlaceId`
 * which first checks the locations collection and then upserts via the AI parser.
 */

import { isMongoObjectIdString } from '@/utils/idGeneration';
import { getCanonicalLocationMongoId } from '@/utils/canonicalLocationId';
import { fetchPersistedLocationByGooglePlaceId } from '@/api/location';
import { debug, error } from '@/utils/logger';

export interface ResolveLocationIdInput {
  /** Mongo ObjectId on the slot itself (locationData._id or location_id field) */
  locationId?: string;
  /** Google Place ID — used as fallback if Mongo id is absent */
  googlePlaceId?: string | null;
  /** Human-readable name for context in the Google resolver */
  name?: string;
  /** Address for context in the Google resolver */
  address?: string;
}

export interface ResolveLocationIdResult {
  locationId: string;
  resolvedViaGoogle: boolean;
}

/**
 * Attempt to resolve a Mongo `locations._id` for a place slot.
 *
 * Returns `null` when:
 * - neither a Mongo id nor a Google Place ID is present, or
 * - the Google resolver call fails or returns no match.
 *
 * Callers should toast an error message and bail when `null` is returned.
 */
export async function resolveLocationIdForSave(
  input: ResolveLocationIdInput,
): Promise<ResolveLocationIdResult | null> {
  const { locationId, googlePlaceId, name, address } = input;

  // 1. Try the direct Mongo ObjectId path.
  const candidateId = getCanonicalLocationMongoId({
    _id: locationId,
    location_id: locationId,
  });

  if (isMongoObjectIdString(candidateId)) {
    debug('[resolveLocationIdForSave] Resolved via direct Mongo id', { locationId: candidateId });
    return { locationId: candidateId, resolvedViaGoogle: false };
  }

  // 2. Fall back to Google Place ID resolution.
  const gid = googlePlaceId?.trim() ?? '';
  if (!gid) {
    error('[resolveLocationIdForSave] No Mongo id or Google Place ID available', { input });
    return null;
  }

  debug('[resolveLocationIdForSave] Falling back to Google resolver', { googlePlaceId: gid, name });

  try {
    const resolved = await fetchPersistedLocationByGooglePlaceId(gid, {
      name: name ?? undefined,
      addressLine: address ?? undefined,
    });

    if (resolved?._id && isMongoObjectIdString(resolved._id)) {
      debug('[resolveLocationIdForSave] Resolved via Google Place ID', {
        googlePlaceId: gid,
        locationId: resolved._id,
      });
      return { locationId: resolved._id, resolvedViaGoogle: true };
    }

    error('[resolveLocationIdForSave] Google resolver returned no valid Mongo id', {
      googlePlaceId: gid,
      resolved,
    });
    return null;
  } catch (err) {
    error('[resolveLocationIdForSave] Google resolver threw', { googlePlaceId: gid, err });
    return null;
  }
}
